import React, {useState} from 'react';
import {Box, Typography, TextField, Chip} from '@mui/material';
import {useSaveDocumentsSelection} from '../../../hooks/productions/useProductions';
import {SaveDocumentsSelectionPayload} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';

const SaveDocumentsSelectionApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(99);
  const [contextIds, setContextIds] = useState<string>(
    '4,5,6,7,8,9,10,11,12,13,14'
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeFamilies, setIncludeFamilies] = useState<boolean>(true);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const {mutate, data, error} = useSaveDocumentsSelection();

  const buildPayload = (): SaveDocumentsSelectionPayload => ({
    productionId,
    isMarkComplete: false,
    securityGroupId,
    exportProductionId: 0,
    productionDocumentsSelectionRequest: {
      totalNumberOfDocsIncludingFamily: 0,
      totalNumberOfFamilyDocs: 0,
      toIncludeFamilies: includeFamilies,
      contextType: 'TAGS',
      contextIDs: contextIds
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((n) => Number(n)),
    },
  });

  const handleSubmit = () => {
    mutate(buildPayload());
  };

  const handleCopyJson = async () => {
    const payload = buildPayload();
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h5"
        className="typo-text headline3_size"
        gutterBottom
      >
        Save Documents Selection
      </Typography>
      <Box sx={{display: 'grid', gap: 2, maxWidth: 640, marginTop: 2}}>
        <TextField
          label="Security Group Id"
          type="number"
          value={securityGroupId}
          onChange={(e) => setSecurityGroupId(Number(e.target.value))}
          size="small"
        />
        <TextField
          label="Production Id"
          type="number"
          value={productionId}
          onChange={(e) => setProductionId(Number(e.target.value))}
          size="small"
        />
        <TextField
          label="Context IDs (comma separated)"
          value={contextIds}
          onChange={(e) => setContextIds(e.target.value)}
          size="small"
        />

        <Box sx={{display: 'flex', gap: 1}}>
          <ArrButton
            onClick={handleSubmit}
            disabled={isLoading}
            dataTestId="save-documents-selection-api-test-submit-button">
            Submit
          </ArrButton>
          <ArrButton
            variant="outlined"
            onClick={handleCopyJson}
            dataTestId="save-documents-selection-api-test-copy-payload-json-button">
            Copy Payload JSON
          </ArrButton>
          {copySuccess && <Chip color="success" label="Copied!" size="small" data-testid={`save-documents-selection-chip-copy-success`} />}
        </Box>

        {isLoading && <Typography>Saving...</Typography>}
        {error && (
          <Typography color="error">{(error as Error).message}</Typography>
        )}
        {data && (
          <Box
            component="pre"
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              maxHeight: 320,
              overflow: 'auto',
              fontSize: '12px',
            }}
          >
            {JSON.stringify(data, null, 2)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SaveDocumentsSelectionApiTest;
